import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Autocomplete, Chip, CircularProgress, MenuItem, Select, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material'
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined'
import SellOutlinedIcon from '@mui/icons-material/SellOutlined'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis
} from 'recharts'
import {
  DashboardFiltro,
  FiltrosDisponiveis,
  Pacientes,
  VisaoGeral,
  fetchDashboardFiltros,
  fetchPacientes,
  fetchVisaoGeral
} from '../../services/dashboardService'
import { colors } from '../../utils/colors'
import MapaCidades from '../../components/MapaCidades'
import * as S from './styles'

// ------------------------------------------------------------------ formatação

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const inteiro = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })

const money = (v?: number) => brl.format(v ?? 0)
const plural = (n: number, um: string, varios: string) => `${inteiro.format(n)} ${n === 1 ? um : varios}`
const moneyShort = (v?: number) => {
  const n = v ?? 0
  if (Math.abs(n) >= 1000) return `R$ ${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`
  return brl.format(n)
}

const ROTULOS: Record<string, string> = {
  APROVADO: 'Aprovado',
  APROVADO_PARCIAL: 'Aprovado parcial',
  PENDENTE: 'Pendente',
  RECUSADO: 'Recusado',
  SEM_ORCAMENTO: 'Sem orçamento',
  SEM_STATUS: 'Sem status',
  NAO_INFORMADO: 'Não informado',
  INDICACAO: 'Indicação',
  ANTIGO: 'Antigo',
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  GOOGLE: 'Google',
  FACHADA: 'Fachada',
  RADIO: 'Rádio',
  JORNAL: 'Jornal',
  PANFLETOS: 'Panfletos',
  OUTROS: 'Outros',
  ADULTO: 'Adulto',
  PEDIATRA: 'Pediatra',
  MASCULINO: 'Masculino',
  FEMININO: 'Feminino'
}
const rotulo = (v?: string) => (v ? ROTULOS[v] ?? capitalizeWords(v.replace(/_/g, ' ').toLowerCase()) : '')

function capitalizeWords(text: string) {
  return text.replace(/(^|\s)\S/g, c => c.toUpperCase())
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
function rotuloPeriodo(periodo: string, granularidade: 'DIA' | 'MES') {
  const [ano, mes, dia] = periodo.split('-')
  if (granularidade === 'DIA') return `${dia}/${mes}`
  return `${MESES[Number(mes) - 1]}/${ano.slice(2)}`
}

function truncate(text: string, max = 34) {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

// ------------------------------------------------------------------ período

type Preset = 'MES_ATUAL' | 'MES_ANTERIOR' | 'ANO_ATUAL' | 'ULTIMOS_12_MESES' | 'TUDO' | 'PERSONALIZADO'

const PRESETS: { value: Preset; label: string }[] = [
  { value: 'MES_ATUAL', label: 'Mês atual' },
  { value: 'MES_ANTERIOR', label: 'Mês anterior' },
  { value: 'ANO_ATUAL', label: 'Ano atual' },
  { value: 'ULTIMOS_12_MESES', label: 'Últimos 12 meses' },
  { value: 'TUDO', label: 'Todo o histórico' },
  { value: 'PERSONALIZADO', label: 'Personalizado' }
]

// data local (toISOString usaria UTC e viraria o dia à noite no fuso do Brasil)
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

function datasDoPreset(preset: Preset, disponiveis?: FiltrosDisponiveis | null): { dataInicio: string; dataFim: string } | null {
  const hoje = new Date()
  const y = hoje.getFullYear()
  const m = hoje.getMonth()
  switch (preset) {
    case 'MES_ATUAL':
      return { dataInicio: iso(new Date(y, m, 1)), dataFim: iso(hoje) }
    case 'MES_ANTERIOR':
      return { dataInicio: iso(new Date(y, m - 1, 1)), dataFim: iso(new Date(y, m, 0)) }
    case 'ANO_ATUAL':
      return { dataInicio: iso(new Date(y, 0, 1)), dataFim: iso(hoje) }
    case 'ULTIMOS_12_MESES':
      return { dataInicio: iso(new Date(y - 1, m + 1, 1)), dataFim: iso(hoje) }
    case 'TUDO':
      return { dataInicio: disponiveis?.dataMinima ?? '2022-01-01', dataFim: disponiveis?.dataMaxima ?? iso(hoje) }
    default:
      return null
  }
}

type FiltroState = DashboardFiltro & { preset: Preset }

const STORAGE_KEY = 'DASHBOARD_FILTRO'

// presets relativos ("Mês atual", "Ano atual"...) são recalculados em relação a hoje;
// só "Personalizado" mantém as datas exatamente como o usuário digitou
function atualizarDatasDoPreset(filtro: FiltroState, disponiveis?: FiltrosDisponiveis | null): FiltroState {
  if (filtro.preset === 'PERSONALIZADO') return filtro
  const datas = datasDoPreset(filtro.preset, disponiveis)
  if (!datas || (datas.dataInicio === filtro.dataInicio && datas.dataFim === filtro.dataFim)) return filtro
  return { ...filtro, ...datas }
}

function filtroInicial(): FiltroState {
  try {
    const salvo = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')
    if (salvo?.preset) return atualizarDatasDoPreset(salvo)
  } catch (e) {
    /* ignora storage inválido */
  }
  return { preset: 'MES_ATUAL', ...datasDoPreset('MES_ATUAL')!, categoria: '', servicos: [], cidades: [], captacoes: [] }
}

// ------------------------------------------------------------------ paleta dos gráficos

const SERIE = {
  orcado: '#8A8A8A',
  vendido: colors.main,
  desconto: '#C9A227',
  destaque: '#3E6FB0'
}
const CATEGORIA_COR: Record<string, string> = { ADULTO: colors.main, PEDIATRA: '#3E6FB0' }
const GENERO_COR: Record<string, string> = { MASCULINO: colors.main, FEMININO: '#B05B7A', NAO_INFORMADO: '#8A8A8A' }

const tooltipStyle = { fontSize: 13, borderRadius: 6, border: `1px solid ${colors.fourth}` }

// ------------------------------------------------------------------ componentes auxiliares

function Kpi({ icon, value, label, hint }: { icon: React.ReactNode; value: string; label: string; hint?: string }) {
  return (
    <S.KpiCard>
      <S.KpiIcon>{icon}</S.KpiIcon>
      <div>
        <S.KpiValue>{value}</S.KpiValue>
        <S.KpiLabel>{label}</S.KpiLabel>
        {hint && <S.KpiHint>{hint}</S.KpiHint>}
      </div>
    </S.KpiCard>
  )
}

function Chart({ title, span, empty, scroll, children }: { title: string; span?: number; empty?: boolean; scroll?: boolean; children: React.ReactNode }) {
  return (
    <S.ChartCard span={span}>
      <S.ChartTitle>{title}</S.ChartTitle>
      <S.ChartBody scroll={scroll}>{empty ? <S.Empty>Sem dados para os filtros selecionados</S.Empty> : children}</S.ChartBody>
    </S.ChartCard>
  )
}

/** Rótulo à direita da barra sem quebra de linha (o LabelList padrão quebra o texto na largura da barra). */
function RotuloBarra({ x, y, width, height, value, formatter }: any) {
  if (!value) return null
  return (
    <text x={x + width + 6} y={y + height / 2} dy={4} fontSize={11} fill={colors.second}>
      {formatter(value)}
    </text>
  )
}

/** Barras horizontais (ranking). */
function BarrasHorizontais({
  data,
  nameKey,
  bars,
  formatter = money,
  labelWidth = 150
}: {
  data: any[]
  nameKey: string
  bars: { key: string; name: string; color: string }[]
  formatter?: (v: number) => string
  labelWidth?: number
}) {
  const height = Math.max(240, data.length * (bars.length > 1 ? 40 : 30) + 20)
  // rótulo compacto ("R$ 174,4 mil") para não estourar a largura do card; o tooltip mantém o valor exato
  const rotuloBarra = formatter === money ? moneyShort : formatter
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 96, bottom: 4, left: 4 }} barCategoryGap={bars.length > 1 ? 10 : 6}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey={nameKey} width={labelWidth} tick={{ fontSize: 12, fill: colors.second }} interval={0} />
        <ChartTooltip contentStyle={tooltipStyle} formatter={(v: number) => formatter(v)} />
        {bars.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {bars.map(b => (
          <Bar isAnimationActive={false} key={b.key} dataKey={b.key} name={b.name} fill={b.color} radius={[0, 4, 4, 0]} maxBarSize={22}>
            <LabelList dataKey={b.key} content={(props: any) => <RotuloBarra {...props} formatter={rotuloBarra} />} />
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}

/** Rosca/pizza com legenda. */
function Rosca({
  data,
  nameKey,
  valueKey,
  colorOf,
  formatter = money,
  donut = true
}: {
  data: any[]
  nameKey: string
  valueKey: string
  colorOf: (k: string) => string
  formatter?: (v: number) => string
  donut?: boolean
}) {
  const total = data.reduce((acc, d) => acc + (d[valueKey] || 0), 0)
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie isAnimationActive={false}
          data={data}
          dataKey={valueKey}
          nameKey={nameKey}
          innerRadius={donut ? 55 : 0}
          outerRadius={90}
          paddingAngle={2}
          label={({ value }) => (total ? `${((value / total) * 100).toFixed(0)}%` : '')}
          labelLine={false}
          style={{ fontSize: 12 }}
        >
          {data.map(d => (
            <Cell key={d[nameKey]} fill={colorOf(d[nameKey])} />
          ))}
        </Pie>
        <ChartTooltip contentStyle={tooltipStyle} formatter={(v: number) => formatter(v)} />
        <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v: string) => rotulo(v)} />
      </PieChart>
    </ResponsiveContainer>
  )
}

/** Converte a série por cidade no formato do mapa (bolha = total orçado; tooltip com vendido e quantidades). */
function pontosCidade(porCidade: VisaoGeral['porCidade']) {
  return porCidade
    .filter(c => c.cidade !== '(sem cidade)')
    .map(c => ({
      cidade: c.cidade,
      valor: c.totalOrcado,
      detalhes: [
        { rotulo: 'Total vendido', valor: money(c.totalVendido) },
        { rotulo: 'Orçamentos / vendas', valor: `${inteiro.format(c.qtdOrcamentos)} / ${inteiro.format(c.qtdVendas)}` }
      ]
    }))
}

// ------------------------------------------------------------------ clientes: orçado x vendido

type Cliente = Pacientes['porCliente'][number]
type VisaoClientes = 'TODOS' | 'SO_ORCADO' | 'COM_VENDA'
type OrdemClientes = 'ORCADO' | 'VENDIDO' | 'EM_ABERTO'

const PAGINA_CLIENTES = 50
const emAberto = (c: Cliente) => Math.max(c.totalOrcado - c.totalVendido, 0)
const VALOR_ORDEM: Record<OrdemClientes, (c: Cliente) => number> = {
  ORCADO: c => c.totalOrcado,
  VENDIDO: c => c.totalVendido,
  EM_ABERTO: emAberto
}

/** Lista de todos os clientes com orçamento ou venda no período; "Só orçado" isola quem ainda não comprou. */
function ClientesOrcadoVendido({ data }: { data: Cliente[] }) {
  const [visao, setVisao] = useState<VisaoClientes>('TODOS')
  const [ordem, setOrdem] = useState<OrdemClientes>('ORCADO')
  const [limite, setLimite] = useState(PAGINA_CLIENTES)

  const lista = useMemo(() => {
    const filtrada = data.filter(c => {
      if (visao === 'SO_ORCADO') return c.totalOrcado > 0 && !c.totalVendido
      if (visao === 'COM_VENDA') return c.totalVendido > 0
      return true
    })
    const valor = VALOR_ORDEM[ordem]
    return filtrada.sort((a, b) => valor(b) - valor(a) || b.totalOrcado - a.totalOrcado)
  }, [data, visao, ordem])

  useEffect(() => setLimite(PAGINA_CLIENTES), [data, visao, ordem])

  const totalOrcado = lista.reduce((acc, c) => acc + c.totalOrcado, 0)
  const totalVendido = lista.reduce((acc, c) => acc + c.totalVendido, 0)
  const maximo = lista.reduce((acc, c) => Math.max(acc, c.totalOrcado, c.totalVendido), 0) || 1
  const restantes = lista.length - limite

  return (
    <S.ChartCard span={5}>
      <S.ChartTitle>Total orçado e total vendido por cliente</S.ChartTitle>
      <S.ClientesControles>
        <ToggleButtonGroup size="small" exclusive value={visao} onChange={(_, v) => v && setVisao(v)}>
          <ToggleButton value="TODOS">Todos</ToggleButton>
          <ToggleButton value="SO_ORCADO">Só orçado</ToggleButton>
          <ToggleButton value="COM_VENDA">Com venda</ToggleButton>
        </ToggleButtonGroup>
        <Select size="small" value={ordem} onChange={e => setOrdem(e.target.value as OrdemClientes)}>
          <MenuItem value="ORCADO">Maior orçado</MenuItem>
          <MenuItem value="VENDIDO">Maior vendido</MenuItem>
          <MenuItem value="EM_ABERTO">Maior diferença (orçado − vendido)</MenuItem>
        </Select>
      </S.ClientesControles>
      <S.ClientesResumo>
        <strong>{inteiro.format(lista.length)} clientes</strong>
        <span>
          <S.Marcador cor={SERIE.orcado} /> Orçado {money(totalOrcado)}
        </span>
        <span>
          <S.Marcador cor={SERIE.vendido} /> Vendido {money(totalVendido)}
        </span>
      </S.ClientesResumo>
      <S.ChartBody scroll>
        {!lista.length && <S.Empty>Sem dados para os filtros selecionados</S.Empty>}
        {lista.slice(0, limite).map(c => (
          <S.ClienteLinha key={c.pacienteId}>
            <S.ClienteNome>
              <Link to={`/pacientes/info/${c.pacienteId}`} target="_blank" rel="noreferrer" title="Abrir cadastro do paciente">
                {capitalizeWords(c.nome.toLowerCase())}
              </Link>
              <span>
                {plural(c.qtdOrcamentos ?? 0, 'orçamento', 'orçamentos')} · {plural(c.qtdVendas, 'venda', 'vendas')}
              </span>
            </S.ClienteNome>
            <div>
              <S.ClienteBarra>
                <S.ClienteBarraValor cor={SERIE.orcado} proporcao={c.totalOrcado / maximo} />
                <span>{money(c.totalOrcado)}</span>
              </S.ClienteBarra>
              <S.ClienteBarra>
                <S.ClienteBarraValor cor={SERIE.vendido} proporcao={c.totalVendido / maximo} />
                <span>{money(c.totalVendido)}</span>
              </S.ClienteBarra>
            </div>
          </S.ClienteLinha>
        ))}
        {restantes > 0 && (
          <S.MostrarMais type="button" onClick={() => setLimite(l => l + PAGINA_CLIENTES * 2)}>
            Mostrar mais ({inteiro.format(restantes)} restantes)
          </S.MostrarMais>
        )}
      </S.ChartBody>
    </S.ChartCard>
  )
}

// ------------------------------------------------------------------ página

function Dashboard() {
  const [aba, setAba] = useState<'geral' | 'pacientes'>('geral')
  const [filtro, setFiltro] = useState<FiltroState>(filtroInicial)
  const [disponiveis, setDisponiveis] = useState<FiltrosDisponiveis | null>(null)
  const [geral, setGeral] = useState<VisaoGeral | null>(null)
  const [pacientes, setPacientes] = useState<Pacientes | null>(null)
  const [carregando, setCarregando] = useState(false)

  useEffect(() => {
    fetchDashboardFiltros()
      .then(d => {
        setDisponiveis(d)
        // "Todo o histórico" depende das datas mínima/máxima vindas da API
        setFiltro(f => (f.preset === 'TUDO' ? atualizarDatasDoPreset(f, d) : f))
      })
      .catch(() => setDisponiveis(null))
  }, [])

  // se a aba ficou aberta e o dia virou, o preset relativo acompanha a data de hoje ao voltar para a página
  useEffect(() => {
    const aoVoltar = () => {
      if (document.visibilityState === 'visible') setFiltro(f => atualizarDatasDoPreset(f, disponiveis))
    }
    document.addEventListener('visibilitychange', aoVoltar)
    window.addEventListener('focus', aoVoltar)
    return () => {
      document.removeEventListener('visibilitychange', aoVoltar)
      window.removeEventListener('focus', aoVoltar)
    }
  }, [disponiveis])

  // busca com pequeno debounce a cada mudança de filtro/aba
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtro))
    const { preset, ...params } = filtro
    let ativo = true
    const timer = setTimeout(async () => {
      setCarregando(true)
      try {
        if (aba === 'geral') {
          const r = await fetchVisaoGeral(params)
          if (ativo) setGeral(r)
        } else {
          const r = await fetchPacientes(params)
          if (ativo) setPacientes(r)
        }
      } catch (e) {
        /* erros já são notificados pelo interceptor da api */
      } finally {
        if (ativo) setCarregando(false)
      }
    }, 300)
    return () => {
      ativo = false
      clearTimeout(timer)
    }
  }, [aba, filtro])

  const setPreset = (preset: Preset) => {
    const datas = datasDoPreset(preset, disponiveis)
    setFiltro(f => ({ ...f, preset, ...(datas ?? {}) }))
  }

  const setData = (campo: 'dataInicio' | 'dataFim', valor: string) => {
    setFiltro(f => ({ ...f, preset: 'PERSONALIZADO', [campo]: valor }))
  }

  const servicoOptions = useMemo(() => disponiveis?.servicos ?? [], [disponiveis])
  const cidadeOptions = useMemo(() => disponiveis?.cidades.map(c => c.cidade) ?? [], [disponiveis])
  const captacaoOptions = useMemo(() => disponiveis?.captacoes ?? [], [disponiveis])
  const servicosSelecionados = useMemo(
    () => servicoOptions.filter(s => filtro.servicos?.includes(s.id)),
    [servicoOptions, filtro.servicos]
  )

  const periodoTexto = `${filtro.dataInicio?.split('-').reverse().join('/')} a ${filtro.dataFim?.split('-').reverse().join('/')}`

  return (
    <S.FullPage>
      {carregando && (
        <S.LoadingOverlay>
          <CircularProgress size={16} /> Atualizando…
        </S.LoadingOverlay>
      )}

      <S.Header>
        <div>
          <S.TitlePage>Dashboard</S.TitlePage>
          <S.Subtitle>Período: {periodoTexto}</S.Subtitle>
        </div>
        <Tabs value={aba} onChange={(_, v) => setAba(v)} textColor="primary" indicatorColor="primary">
          <Tab value="geral" label="Visão geral" />
          <Tab value="pacientes" label="Pacientes" />
        </Tabs>
      </S.Header>

      <S.FilterBar>
        <S.FilterField>
          Período
          <Select size="small" value={filtro.preset} onChange={e => setPreset(e.target.value as Preset)}>
            {PRESETS.map(p => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
        </S.FilterField>
        <S.FilterField>
          Data início
          <TextField size="small" type="date" value={filtro.dataInicio ?? ''} onChange={e => setData('dataInicio', e.target.value)} />
        </S.FilterField>
        <S.FilterField>
          Data fim
          <TextField size="small" type="date" value={filtro.dataFim ?? ''} onChange={e => setData('dataFim', e.target.value)} />
        </S.FilterField>
        <S.FilterField>
          Categoria
          <Select size="small" value={filtro.categoria ?? ''} displayEmpty onChange={e => setFiltro(f => ({ ...f, categoria: e.target.value }))}>
            <MenuItem value="">Todas</MenuItem>
            {(disponiveis?.categorias ?? ['ADULTO', 'PEDIATRA']).map(c => (
              <MenuItem key={c} value={c}>
                {rotulo(c)}
              </MenuItem>
            ))}
          </Select>
        </S.FilterField>
        <S.FilterField>
          Tipo de serviço
          <Autocomplete
            multiple
            size="small"
            options={servicoOptions}
            value={servicosSelecionados}
            groupBy={o => rotulo(o.categoria)}
            getOptionLabel={o => o.nome}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, v) => setFiltro(f => ({ ...f, servicos: v.map(s => s.id) }))}
            renderTags={(value, getTagProps) =>
              value.map((o, i) => <Chip {...getTagProps({ index: i })} key={o.id} size="small" label={truncate(o.nome, 22)} />)
            }
            renderInput={params => <TextField {...params} placeholder={filtro.servicos?.length ? '' : 'Todos'} />}
          />
        </S.FilterField>
        <S.FilterField>
          Localização
          <Autocomplete
            multiple
            size="small"
            options={cidadeOptions}
            value={filtro.cidades ?? []}
            getOptionLabel={c => capitalizeWords(c)}
            onChange={(_, v) => setFiltro(f => ({ ...f, cidades: v }))}
            renderTags={(value, getTagProps) =>
              value.map((c, i) => <Chip {...getTagProps({ index: i })} key={c} size="small" label={capitalizeWords(c)} />)
            }
            renderInput={params => <TextField {...params} placeholder={filtro.cidades?.length ? '' : 'Todas'} />}
          />
        </S.FilterField>
        <S.FilterField>
          Captação
          <Autocomplete
            multiple
            size="small"
            options={captacaoOptions}
            value={filtro.captacoes ?? []}
            getOptionLabel={c => rotulo(c)}
            onChange={(_, v) => setFiltro(f => ({ ...f, captacoes: v }))}
            renderTags={(value, getTagProps) =>
              value.map((c, i) => <Chip {...getTagProps({ index: i })} key={c} size="small" label={rotulo(c)} />)
            }
            renderInput={params => <TextField {...params} placeholder={filtro.captacoes?.length ? '' : 'Todas'} />}
          />
        </S.FilterField>
      </S.FilterBar>

      {aba === 'geral' && <VisaoGeralView data={geral} />}
      {aba === 'pacientes' && <PacientesView data={pacientes} />}
    </S.FullPage>
  )
}

// ------------------------------------------------------------------ aba: visão geral

function VisaoGeralView({ data }: { data: VisaoGeral | null }) {
  if (!data) return null
  const k = data.kpis
  const servicos = data.servicosPorTipo.map(s => ({ ...s, nomeCurto: truncate(s.nome) }))
  const categorias = data.porCategoria.map(c => ({ ...c, nome: rotulo(c.categoria) }))
  const cidades = pontosCidade(data.porCidade)
  const periodo = data.porPeriodo.map(p => ({ ...p, nome: rotuloPeriodo(p.periodo, data.granularidade) }))
  const status = data.porStatus.map(s => ({ ...s, nome: rotulo(s.status) }))
  const captacao = data.vendidoPorCaptacao.map(c => ({ ...c, nome: rotulo(c.captacao) }))

  return (
    <>
      <S.KpiGrid>
        <Kpi icon={<RequestQuoteOutlinedIcon />} value={money(k.totalOrcado)} label="Total orçado" />
        <Kpi
          icon={<SellOutlinedIcon />}
          value={money(k.totalVendido)}
          label="Total vendido"
          hint={k.totalCancelado > 0 ? `Não inclui ${money(k.totalCancelado)} em ${plural(k.qtdCanceladas, 'venda cancelada', 'vendas canceladas')}` : undefined}
        />
        <Kpi icon={<AttachMoneyIcon />} value={money(k.ticketMedioVendas)} label="Ticket médio por venda" />
        <Kpi icon={<ReceiptLongOutlinedIcon />} value={inteiro.format(k.qtdOrcamentos)} label="Qtde. orçamentos" />
        <Kpi icon={<ShoppingCartOutlinedIcon />} value={inteiro.format(k.qtdVendas)} label="Qtde. vendas" />
      </S.KpiGrid>

      <S.ChartGrid>
        <Chart title="Quantidade de serviços por tipo de serviço" span={5} empty={!servicos.length} scroll>
          <BarrasHorizontais
            data={servicos}
            nameKey="nomeCurto"
            labelWidth={210}
            formatter={v => inteiro.format(v)}
            bars={[{ key: 'quantidade', name: 'Quantidade', color: SERIE.destaque }]}
          />
        </Chart>
        <Chart title="Total orçado, vendido e desconto por categoria" span={4} empty={!categorias.length}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={categorias} margin={{ top: 20, right: 10, bottom: 0, left: 10 }} barCategoryGap={30}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.fourth} />
              <XAxis dataKey="nome" tick={{ fontSize: 12, fill: colors.second }} />
              <YAxis tickFormatter={moneyShort} tick={{ fontSize: 11, fill: colors.third }} width={80} />
              <ChartTooltip contentStyle={tooltipStyle} formatter={(v: number) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar isAnimationActive={false} dataKey="totalOrcado" name="Total orçado" fill={SERIE.orcado} radius={[4, 4, 0, 0]} maxBarSize={40} />
              <Bar isAnimationActive={false} dataKey="totalVendido" name="Total vendido" fill={SERIE.vendido} radius={[4, 4, 0, 0]} maxBarSize={40} />
              <Bar isAnimationActive={false} dataKey="totalDesconto" name="Total desconto" fill={SERIE.desconto} radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </Chart>
        <Chart title="Total orçado por cidade" span={3} empty={!cidades.length}>
          <MapaCidades data={cidades} formatter={money} rotuloValor="Total orçado" />
        </Chart>

        <Chart title="Total orçado e total vendido por período" span={6} empty={!periodo.length}>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={periodo} margin={{ top: 10, right: 16, bottom: 0, left: 10 }}>
              <defs>
                <linearGradient id="gradVendido" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SERIE.vendido} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={SERIE.vendido} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gradOrcado" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SERIE.orcado} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={SERIE.orcado} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.fourth} />
              <XAxis dataKey="nome" tick={{ fontSize: 11, fill: colors.second }} minTickGap={16} />
              <YAxis tickFormatter={moneyShort} tick={{ fontSize: 11, fill: colors.third }} width={80} />
              <ChartTooltip contentStyle={tooltipStyle} formatter={(v: number) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area isAnimationActive={false} type="linear" dataKey="totalOrcado" name="Total orçado" stroke={SERIE.orcado} fill="url(#gradOrcado)" strokeWidth={2} />
              <Area isAnimationActive={false} type="linear" dataKey="totalVendido" name="Total vendido" stroke={SERIE.vendido} fill="url(#gradVendido)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Chart>
        <Chart title="Total orçado e total vendido por status" span={3} empty={!status.length}>
          <BarrasHorizontais
            data={status}
            nameKey="nome"
            labelWidth={110}
            bars={[
              { key: 'totalOrcado', name: 'Total orçado', color: SERIE.orcado },
              { key: 'totalVendido', name: 'Total vendido', color: SERIE.vendido }
            ]}
          />
        </Chart>
        <Chart title="Total vendido por meio de captação" span={3} empty={!captacao.length}>
          <BarrasHorizontais data={captacao} nameKey="nome" labelWidth={100} bars={[{ key: 'totalVendido', name: 'Total vendido', color: SERIE.vendido }]} />
        </Chart>
      </S.ChartGrid>
    </>
  )
}

// ------------------------------------------------------------------ aba: pacientes

function PacientesView({ data }: { data: Pacientes | null }) {
  if (!data) return null
  const k = data.kpis
  const idades = data.porIdade
  const cidades = pontosCidade(data.porCidade)
  const generos = data.porGenero
  const categorias = data.porCategoria.filter(c => c.totalVendido > 0 || c.qtdServicos > 0)
  const captacao = data.clientesPorCaptacao.map(c => ({ ...c, nome: rotulo(c.captacao) }))

  return (
    <>
      <S.KpiGrid>
        <Kpi icon={<RequestQuoteOutlinedIcon />} value={money(k.totalOrcado)} label="Total orçado" />
        <Kpi icon={<SellOutlinedIcon />} value={money(k.totalVendido)} label="Total vendido" />
        <Kpi icon={<GroupsOutlinedIcon />} value={inteiro.format(k.coberturaClientes)} label="Cobertura de clientes" />
        <Kpi icon={<PersonOutlineOutlinedIcon />} value={money(k.ticketMedioClientes)} label="Ticket médio por cliente" />
        <Kpi icon={<ShoppingCartOutlinedIcon />} value={inteiro.format(k.qtdVendas)} label="Qtde. vendas" />
      </S.KpiGrid>

      <S.ChartGrid>
        <ClientesOrcadoVendido data={data.porCliente} />
        <Chart title="Total orçado e total vendido por idade" span={4} empty={!idades.length}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={idades} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.fourth} />
              <XAxis dataKey="idade" tick={{ fontSize: 11, fill: colors.second }} minTickGap={8} />
              <YAxis tickFormatter={moneyShort} tick={{ fontSize: 11, fill: colors.third }} width={80} />
              <ChartTooltip contentStyle={tooltipStyle} formatter={(v: number) => money(v)} labelFormatter={l => `${l} anos`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar isAnimationActive={false} dataKey="totalOrcado" name="Total orçado" fill={SERIE.orcado} maxBarSize={14} />
              <Bar isAnimationActive={false} dataKey="totalVendido" name="Total vendido" fill={SERIE.vendido} maxBarSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </Chart>
        <Chart title="Total orçado por cidade" span={3} empty={!cidades.length}>
          <MapaCidades data={cidades} formatter={money} rotuloValor="Total orçado" />
        </Chart>

        <Chart title="Total vendido por gênero" span={3} empty={!generos.length}>
          <Rosca data={generos} nameKey="genero" valueKey="totalVendido" colorOf={g => GENERO_COR[g] ?? '#8A8A8A'} donut={false} />
        </Chart>
        <Chart title="Total vendido por categoria" span={3} empty={!categorias.length}>
          <Rosca data={categorias} nameKey="categoria" valueKey="totalVendido" colorOf={c => CATEGORIA_COR[c] ?? '#8A8A8A'} />
        </Chart>
        <Chart title="Qtde. serviços por categoria" span={3} empty={!categorias.length}>
          <Rosca data={categorias} nameKey="categoria" valueKey="qtdServicos" colorOf={c => CATEGORIA_COR[c] ?? '#8A8A8A'} formatter={v => inteiro.format(v)} />
        </Chart>
        <Chart title="Qtde. de clientes por meio de captação" span={3} empty={!captacao.length}>
          <BarrasHorizontais
            data={captacao}
            nameKey="nome"
            labelWidth={100}
            formatter={v => inteiro.format(v)}
            bars={[{ key: 'qtdClientes', name: 'Clientes', color: SERIE.destaque }]}
          />
        </Chart>
      </S.ChartGrid>
    </>
  )
}

export default Dashboard
